import React, { useEffect, useState } from 'react';
import { siftApiClient } from '../../api/siftApiClient';
import {
  BROKER_STATUS_CHOICES, BROKER_STATUS_HELP, BROKER_STATUS_UNSET_LABEL, statusSetterName,
} from './brokerVinStatus';

interface Props {
  vin: string;
  /** Supply when the caller already has it (the carousel row does); otherwise the
   *  control fetches its own, since the VIN-detail payload carries no status. */
  status?: string | null;
  statusBy?: string | null;
  statusAt?: string | null;
  /** Lets the opener keep its own row in step without refetching the board. */
  onChanged?: (vin: string, status: string | null) => void;
  className?: string;
}

/**
 * §6.5 broker-group VIN status, editable wherever a VIN is on screen. Extracted
 * from the carousel table so the pop-out does not grow a second copy that can
 * drift from it.
 */
export const BrokerVinStatusControl: React.FC<Props> = ({
  vin, status, statusBy, statusAt, onChanged, className,
}) => {
  const given = status !== undefined;
  const [value, setValue] = useState<string>(status || '');
  const [by, setBy] = useState<string | null>(statusBy ?? null);
  const [at, setAt] = useState<string | null>(statusAt ?? null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setError(null);
    if (given) { setValue(status || ''); setBy(statusBy ?? null); setAt(statusAt ?? null); return; }
    if (!vin) return;
    const controller = new AbortController();
    let cancelled = false;
    (async () => {
      try {
        const got = await siftApiClient.getBrokerVinStatus(vin, controller.signal);
        if (cancelled) return;
        setValue(got.status || '');
        setBy(got.by ?? null);
        setAt(got.at ?? null);
      } catch {
        // Unreadable status must not block setting one.
      }
    })();
    return () => { cancelled = true; controller.abort(); };
  }, [vin, given, status, statusBy, statusAt]);

  const update = async (next: string) => {
    const clean = next.trim();
    const prior = value;
    setValue(clean);          // optimistic: the select must not lag the click
    setSaving(true);
    setError(null);
    try {
      if (clean) await siftApiClient.setBrokerVinStatus(vin, clean);
      else await siftApiClient.clearBrokerVinStatus(vin);
      onChanged?.(vin, clean || null);
    } catch (err: any) {
      setValue(prior);
      setError(err?.response?.data?.detail || 'Could not save status');
    } finally {
      setSaving(false);
    }
  };

  const setter = statusSetterName(by);
  const when = at ? new Date(at).toLocaleDateString() : null;

  return (
    <div className={`bvs-wrap${className ? ` ${className}` : ''}`}>
      <label className="bvs-label" htmlFor={`bvs-${vin}`}>Status</label>
      <select
        id={`bvs-${vin}`}
        className="bvs-select"
        value={value}
        disabled={saving}
        title={BROKER_STATUS_HELP}
        onChange={(e) => void update(e.target.value)}
      >
        <option value="">{BROKER_STATUS_UNSET_LABEL}</option>
        {BROKER_STATUS_CHOICES.map((s) => (
          <option key={s} value={s}>{s}</option>
        ))}
      </select>
      {error
        ? <span className="bvs-error" role="alert">{error}</span>
        : setter && value
          ? <span className="bvs-by">by {setter}{when ? ` · ${when}` : ''}</span>
          : null}
    </div>
  );
};
